import React from 'react';
import styled, { keyframes } from 'styled-components';
import { Card, CardHeader, CardBody, CardTitle, CardFooter, CardBadge } from '../../../styles/components/ModernCard';

// Subtle glow animation for approvals that need attention
const warningPulse = keyframes`
  0% { box-shadow: 0 0 0 rgba(255, 193, 7, 0.1); }
  50% { box-shadow: 0 0 10px rgba(255, 193, 7, 0.3); }
  100% { box-shadow: 0 0 0 rgba(255, 193, 7, 0.1); }
`;

// Card border glow animation
const cardGlow = keyframes`
  0% { box-shadow: 0 10px 30px rgba(15, 23, 42, 0.1), 0 0 0 1px rgba(255, 193, 7, 0.1); }
  50% { box-shadow: 0 10px 30px rgba(15, 23, 42, 0.15), 0 0 0 1px rgba(255, 193, 7, 0.3); }
  100% { box-shadow: 0 10px 30px rgba(15, 23, 42, 0.1), 0 0 0 1px rgba(255, 193, 7, 0.1); }
`;

const ActionButton = styled.button`
  padding: 0.75rem 1.5rem;
  background-color: ${props => props.primary ? 'rgba(79, 70, 229, 0.85)' : 'rgba(15, 23, 42, 0.3)'};
  color: ${props => props.primary ? 'white' : props.theme.colors.primary};
  border: ${props => props.primary ? '1px solid rgba(99, 102, 241, 0.5)' : '1.5px solid rgba(99, 102, 241, 0.6)'};
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  position: relative;
  overflow: hidden;
  letter-spacing: 0.5px;
  backdrop-filter: blur(8px);
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      135deg,
      ${props => props.primary 
        ? 'rgba(129, 140, 248, 0.2) 0%, rgba(99, 102, 241, 0.2) 50%, rgba(79, 70, 229, 0.1) 100%'
        : 'rgba(255, 255, 255, 0.1) 0%, rgba(99, 102, 241, 0.1) 50%, rgba(15, 23, 42, 0.1) 100%'}
    );
    z-index: -1;
  }
  
  &:after {
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    top: -100%;
    left: -100%;
    background: linear-gradient(
      45deg,
      transparent 0%,
      rgba(255, 255, 255, 0.1) 45%,
      rgba(255, 255, 255, 0.3) 50%,
      rgba(255, 255, 255, 0.1) 55%,
      transparent 100%
    );
    transition: all 0.6s ease;
    z-index: 1;
  }
  
  &:hover {
    background-color: ${props => props.primary ? 'rgba(79, 70, 229, 0.95)' : 'rgba(99, 102, 241, 0.15)'};
    transform: translateY(-2px);
    box-shadow: ${props => props.primary 
      ? '0 8px 25px -5px rgba(79, 70, 229, 0.5), 0 0 0 2px rgba(129, 140, 248, 0.3)'
      : '0 7px 20px -5px rgba(31, 38, 135, 0.3), 0 0 0 2px rgba(99, 102, 241, 0.1)'};
    
    &:after {
      top: 100%;
      left: 100%;
    }
  }
  
  &:active {
    transform: translateY(0);
  }
  
  svg {
    font-size: 1rem;
  }
`;

const ApprovalItem = styled.div`
  padding: 0.85rem;
  border-radius: 10px;
  margin-bottom: 0.85rem;
  border-left: 3px solid rgba(255, 193, 7, 0.6);
  background: rgba(15, 23, 42, 0.25);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: all 0.3s ease;
  position: relative;
  animation: ${warningPulse} 4s infinite ease-in-out;
  
  /* Space-themed subtle glimmer effect */
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: inherit;
    background: 
      linear-gradient(
        120deg,
        transparent 0%,
        rgba(255, 193, 7, 0.03) 40%,
        rgba(255, 140, 0, 0.05) 60%,
        transparent 100%
      );
    z-index: -1;
    opacity: 0;
    transition: opacity 0.5s ease;
  }
  
  &:hover {
    transform: translateX(3px);
    border-left-color: rgba(255, 193, 7, 0.8);
    box-shadow: 0 5px 15px rgba(15, 23, 42, 0.1);
    
    &:before {
      opacity: 1;
    }
  }
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const ApprovalContent = styled.div`
  flex: 1;
`;

const ApprovalTitle = styled.div`
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  letter-spacing: 0.3px;
`;

const ApprovalCount = styled.div`
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.5);
  letter-spacing: 0.2px;
  margin-top: 0.25rem;
`;

const ApprovalAction = styled.button`
  padding: 0.4rem 0.75rem;
  background: rgba(255, 193, 7, 0.2);
  color: rgba(255, 255, 255, 0.95);
  border: 1px solid rgba(255, 193, 7, 0.3);
  border-radius: 6px;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  backdrop-filter: blur(4px);
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      135deg,
      rgba(255, 193, 7, 0.2) 0%,
      rgba(255, 193, 7, 0.1) 50%,
      rgba(255, 193, 7, 0.05) 100%
    );
    z-index: -1;
  }
  
  &:hover {
    background: rgba(255, 193, 7, 0.3);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(255, 193, 7, 0.2), 0 0 0 2px rgba(255, 193, 7, 0.1);
    
    &:after {
      left: 150%;
    }
  }
  
  &:after {
    content: '';
    position: absolute;
    top: -50%;
    left: -60%;
    width: 20%;
    height: 200%;
    background-color: rgba(255, 255, 255, 0.1);
    transform: rotate(25deg);
    transition: left 0.6s ease;
  }
  
  &:active {
    transform: translateY(0);
  }
`;

// Styled Card with space theme
const SpaceCard = styled(Card)`
  background: rgba(15, 23, 42, 0.65);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 193, 7, 0.15);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  animation: ${cardGlow} 8s infinite ease-in-out;
  position: relative;
  z-index: 1;
  
  /* Space background gradients */
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle at 20% 20%, rgba(255, 193, 7, 0.03) 0%, transparent 50%),
      radial-gradient(circle at 80% 80%, rgba(255, 140, 0, 0.02) 0%, transparent 50%);
    border-radius: inherit;
    z-index: -1;
  }
  
  /* Star-like dots in background */
  &:after {
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    background-image: 
      radial-gradient(1px 1px at 15% 15%, rgba(255, 255, 255, 0.7) 1px, transparent 0),
      radial-gradient(1px 1px at 35% 45%, rgba(255, 255, 255, 0.6) 1px, transparent 0),
      radial-gradient(1px 1px at 55% 25%, rgba(255, 255, 255, 0.7) 1px, transparent 0),
      radial-gradient(1px 1px at 75% 65%, rgba(255, 255, 255, 0.6) 1px, transparent 0),
      radial-gradient(1.5px 1.5px at 90% 10%, rgba(255, 255, 255, 0.7) 1px, transparent 0);
    border-radius: inherit;
    z-index: -1;
    opacity: 0.3;
  }
`;

// Styled Card Header with space theme
const SpaceCardHeader = styled(CardHeader)`
  border-bottom: 1px solid rgba(255, 193, 7, 0.15);
  padding: 1.25rem 1.5rem;
`;

// Styled Card Title with space theme
const SpaceCardTitle = styled(CardTitle)`
  color: rgba(255, 255, 255, 0.9);
  font-size: 1.25rem;
  letter-spacing: 0.5px;
  position: relative;
  display: inline-block;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -5px;
    left: 0;
    width: 40px;
    height: 2px;
    background: linear-gradient(
      to right,
      rgba(255, 193, 7, 0.7),
      rgba(255, 140, 0, 0.5)
    );
    border-radius: 2px;
    box-shadow: 0 0 8px rgba(255, 193, 7, 0.5);
  }
`;

// Styled Card Badge with space theme
const SpaceCardBadge = styled(CardBadge)`
  background: rgba(255, 193, 7, 0.15);
  color: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(255, 193, 7, 0.3);
  font-weight: 600;
  letter-spacing: 0.5px;
  padding: 0.35rem 0.8rem;
  border-radius: 6px;
  position: relative;
  overflow: hidden;
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      135deg,
      rgba(255, 193, 7, 0.1) 0%,
      rgba(255, 140, 0, 0.05) 100%
    );
    z-index: -1;
  }
`;

// Styled Card Footer with space theme
const SpaceCardFooter = styled(CardFooter)`
  border-top: 1px solid rgba(255, 193, 7, 0.15);
  padding: 1.25rem 1.5rem;
`;

const ApprovalsList = ({ approvals, onProcessAll}) => {
  return (
    <SpaceCard>
      <SpaceCardHeader>
        <SpaceCardTitle>Pending Approvals</SpaceCardTitle>
        <SpaceCardBadge warning>Requires Action</SpaceCardBadge>
      </SpaceCardHeader>
      <CardBody>
        {approvals.map((approval, index) => (
          <ApprovalItem key={index}>
            <ApprovalContent>
              <ApprovalTitle>{approval.title}</ApprovalTitle>
              <ApprovalCount>{approval.count}</ApprovalCount>
            </ApprovalContent>
          </ApprovalItem>
        ))}
      </CardBody>
      <SpaceCardFooter centered>
        <ActionButton primary onClick={onProcessAll}>
          Process All Approvals
        </ActionButton>
      </SpaceCardFooter>
    </SpaceCard>
  );
};

export default ApprovalsList;
